import React, { useEffect, useState } from 'react';
import {
  Smartphone,
  Tablet,
  Bell,
  MapPin,
  BarChart3,
  Clock,
  CheckCircle,
  Shield,
  Zap,
  ArrowRight,
  Globe,
  Users,
  Activity,
  Download,
  Cloud,
  MessageSquare,
  Route,
  Settings,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const mobileHighlights = [
  {
    title: 'Real-Time Notifications',
    value: 'Instant Alerts',
    icon: Bell,
    description: 'Get push notifications for vehicle alerts, geofence breaches, and maintenance reminders directly on your mobile device.',
    color: 'from-orange-500 to-red-500',
  },
  {
    title: 'Live Fleet Tracking',
    value: '24/7 Access',
    icon: MapPin,
    description: 'Monitor your entire fleet in real-time with interactive maps, route history, and location updates anywhere, anytime.',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'On-the-Go Reports',
    value: 'Mobile Analytics',
    icon: BarChart3,
    description: 'View fleet performance metrics, driver behavior reports, and expense summaries with one-tap access from your phone.',
    color: 'from-purple-500 to-pink-500',
  },
];

const mobileFeatures = [
  {
    icon: MapPin,
    title: 'Live Location Tracking',
    description:
      'Track your vehicles in real-time with interactive maps, geofencing alerts, and route optimization. Get instant location updates and historical route playback on your mobile device.',
    image:
      'https://images.unsplash.com/photo-1731082154898-2e63df0f2a42?w=1200&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bW9iaWxlJTIwbWFwfGVufDB8fDB8fHww',
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    description:
      'Receive instant push notifications for critical events, driver alerts, geofence breaches, maintenance reminders, and emergency situations. Customize alert preferences for different user roles.',
    image:
      'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=1200&auto=format&fit=crop&q=60',
  },
  {
    icon: BarChart3,
    title: 'Mobile Dashboards',
    description:
      'Access comprehensive fleet analytics, driver scorecards, fuel consumption reports, and performance metrics from your smartphone. View key insights at a glance with intuitive mobile dashboards.',
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=60',
  },
];

const mobilePillars = [
  {
    title: 'Cross-Platform Support',
    description:
      'Native mobile apps for iOS and Android with responsive web app for any device. Consistent experience across smartphones, tablets, and wearables with offline mode capabilities.',
    points: [
      'Native iOS and Android apps with platform-specific optimizations',
      'Progressive Web App (PWA) for browser-based access',
      'Offline mode for viewing cached data without internet',
      'Apple Watch and Wear OS support for quick notifications',
    ],
    icon: Globe,
  },
  {
    title: 'Quick Actions & Commands',
    description:
      'Execute critical fleet operations directly from your mobile device with one-tap commands, voice controls, and customizable quick actions for common tasks.',
    points: [
      'One-tap vehicle commands (lock, unlock, start, stop)',
      'Voice-controlled navigation and search',
      'Quick report generation and sharing via email/SMS',
      'Emergency SOS button for instant help requests',
    ],
    icon: Zap,
  },
  {
    title: 'Team Collaboration',
    description:
      'Enable seamless communication between dispatchers, drivers, and managers with in-app messaging, task assignments, and shared location sharing for improved coordination.',
    points: [
      'In-app messaging and group chat for team communication',
      'Task assignment and status updates in real-time',
      'Driver check-in/check-out with photo verification',
      'Shared location tracking for fleet visibility',
    ],
    icon: Users,
  },
];

const MobileManagementPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#0b1120] via-[#1a1f37] to-[#2d1b4e] text-slate-100'
          : 'bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}
    >
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Mobile Management" />

      <div className="absolute inset-0 pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-[160px] animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] bg-blue-500/20 rounded-full blur-[200px] animate-pulse delay-500" />
          </>
        ) : (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/3 w-[30rem] h-[30rem] bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
          </>
        )}
      </div>

      <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <header className="space-y-5 text-center">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${
                theme === 'dark'
                  ? 'bg-slate-900 border-1 border-muted-foreground/20 text-orange-200'
                  : 'bg-white border-1 border-muted-foreground/20 text-orange-700'
              }`}
            >
              <Smartphone className="h-4 w-4" />
              Mobile Fleet Control
            </div>
            <h1 className="text-sm sm:text-4xl md:text-4xl font-bold leading-tight">
              Manage Your Fleet from Anywhere, Anytime
            </h1>
            <p
              className={`text-sm sm:text-lg md:text-xl max-w-3xl mx-auto ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz mobile app puts complete fleet management in your pocket. Monitor vehicles,
              respond to alerts, and make critical decisions on-the-go with our intuitive mobile platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://apps.apple.com/trackyourwheelz', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                Download iOS App
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('https://play.google.com/trackyourwheelz', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Download Android App
              </Button>
            </div>
          </header>

          <section className="grid gap-6 md:grid-cols-3">
            {mobileHighlights.map((highlight) => {
              const Icon = highlight.icon;
              return (
                <Card
                  key={highlight.title}
                  className={`border-1 border-muted-foreground/20 ${
                    theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                  } shadow-lg`}
                >
                  <CardHeader className="space-y-3">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${highlight.color} flex items-center justify-center`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-sm sm:text-base font-semibold">
                      {highlight.title}
                    </CardTitle>
                    <CardDescription
                      className={`text-2xl sm:text-3xl font-bold ${
                        theme === 'dark' ? 'text-orange-200' : 'text-orange-600'
                      }`}
                    >
                      {highlight.value}
                    </CardDescription>
                  </CardHeader>
                  <CardContent
                    className={`text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                    }`}
                  >
                    {highlight.description}
                  </CardContent>
                </Card>
              );
            })}
          </section>

          <section className="space-y-6">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Mobile Features</h2>
            <p
              className={`text-sm sm:text-base max-w-3xl mx-auto text-center ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Powerful mobile capabilities designed for fleet managers, dispatchers, and drivers who
              need instant access to fleet operations from anywhere.
            </p>
            <div className="grid gap-6 lg:grid-cols-3">
              {mobileFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className={`overflow-hidden border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <div className="relative h-40">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src =
                            'https://images.unsplash.com/photo-1731082154898-2e63df0f2a42?w=1200&auto=format&fit=crop&q=60';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-semibold">{feature.title}</span>
                      </div>
                    </div>
                    <CardContent
                      className={`p-5 text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {feature.description}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-8">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Mobile Capabilities</h2>
            <div className="space-y-6">
              {mobilePillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <Card
                    key={pillar.title}
                    className={`border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                      <div
                        className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                          theme === 'dark'
                            ? 'bg-orange-500/20 text-orange-200'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="text-sm sm:text-lg font-semibold">
                          {pillar.title}
                        </CardTitle>
                        <CardDescription
                          className={`text-sm ${
                            theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                          }`}
                        >
                          {pillar.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <ul
                        className={`grid gap-3 sm:grid-cols-2 text-sm ${
                          theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {pillar.points.map((point) => (
                          <li key={point} className="flex items-start gap-2">
                            <CheckCircle className="mt-1 h-4 w-4 text-orange-500 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-blue-500" />
                  Offline Mode
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Continue working even without internet connectivity. View cached data, offline maps,
                  and sync changes automatically when connection is restored.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Our mobile app stores critical fleet data locally, allowing you to access vehicle
                  locations, routes, and reports even in areas with poor connectivity.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="mt-1 h-4 w-4 text-blue-500" />
                    Cached maps and vehicle locations available offline
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="mt-1 h-4 w-4 text-blue-500" />
                    Automatic sync when connection is restored
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="mt-1 h-4 w-4 text-blue-500" />
                    Secure local data storage with encryption
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5 text-blue-500" />
                  App Customization
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Personalize your mobile experience with customizable dashboards, widget layouts, and
                  notification preferences tailored to your role and workflow.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Configure the app to match your workflow with role-based views, custom widgets, and
                  personalized alert settings.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Activity className="mt-1 h-4 w-4 text-blue-500" />
                    Customizable home screen widgets and shortcuts
                  </li>
                  <li className="flex items-start gap-2">
                    <Route className="mt-1 h-4 w-4 text-blue-500" />
                    Role-based views for managers, dispatchers, and drivers
                  </li>
                  <li className="flex items-start gap-2">
                    <MessageSquare className="mt-1 h-4 w-4 text-blue-500" />
                    Personalized notification preferences and quiet hours
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="rounded-3xl overflow-hidden relative">
            <img
              src="https://images.unsplash.com/photo-1731082154898-2e63df0f2a42?w=1600&auto=format&fit=crop&q=60"
              alt="Mobile fleet management"
              className="h-72 sm:h-80 w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-y-0 left-0 px-6 sm:px-10 py-8 sm:py-10 flex flex-col justify-between text-white max-w-xl">
              <div className="space-y-3">
                <h2 className="text-sm sm:text-3xl font-bold">Fleet management in your pocket</h2>
                <p className="text-sm sm:text-base opacity-80">
                  Whether you're in the office, on the road, or at home, stay connected to your fleet
                  with our powerful mobile platform available on iOS, Android, and web browsers.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
                <span className="rounded-full border border-white/40 px-4 py-1">iOS & Android</span>
                <span className="rounded-full border border-white/40 px-4 py-1">PWA support</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Offline mode</span>
              </div>
            </div>
          </section>

          <footer
            className={`rounded-2xl border-1 border-muted-foreground/20 p-6 sm:p-8 text-center ${
              theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
            }`}
          >
            <h2 className="text-sm sm:text-3xl font-bold mb-4">Ready to manage your fleet on mobile?</h2>
            <p
              className={`text-sm sm:text-base max-w-2xl mx-auto mb-6 ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Download TrackYourWheelz mobile app and take complete control of your fleet operations
              from your smartphone or tablet. Available on iOS and Android platforms.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://apps.apple.com/trackyourwheelz', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                <Download className="mr-2 h-4 w-4" />
                Download for iOS
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('https://play.google.com/trackyourwheelz', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                <Download className="mr-2 h-4 w-4" />
                Download for Android
              </Button>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default MobileManagementPage;
